import styled, { keyframes } from 'styled-components';
import React, { useState, useEffect } from 'react';
import Logo from 'assets/images/Logo blue.png'
import { Loader } from "semantic-ui-react";
import { useSelector, useDispatch, shallowEqual } from "react-redux";
import { SG_GET_CHAT_GPT_SUMMARY } from "constants/actions";
import { v4 as uuidv4 } from "uuid";
// Text constants
const ANALYSIS_TITLE = "AI Analysis";
const SENTIMENT = "Sentiment";
const THEMES = "Key Themes";
const ASPECTS = "Key Aspects Mentioned";
const NO_FEEDBACK = "No feedback provided to analyze.";
const OVERALL_SUMMARY = "Overall Summary";
const LOADING_ANALYSIS = "Analyzing feedback...";
const ERROR_MESSAGE = "An error occurred while analyzing the feedback.";
const ANALYSIS_DESCRIPTION = "We've analyzed your feedback using AI to identify key themes, sentiments, and aspects mentioned.";

const Finish = ({ responses, tone = 'balanced' }) => {
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(true);
    const [individualAnalyses, setIndividualAnalyses] = useState([]);
    const [overallAnalysis, setOverallAnalysis] = useState(null);
    const [analysisStatus, setAnalysisStatus] = useState({
        inProgress: true,
        individualCount: 0,
        summaryComplete: false,
        error: false,
        analysisMode: 'none' // Can be 'none', 'individual', or 'summary'
    });
    // New state to track which feedback item we're currently analyzing
    const [currentAnalysisIndex, setCurrentAnalysisIndex] = useState(-1);
    // New state to track if we're waiting for API response
    const [waitingForResponse, setWaitingForResponse] = useState(false);
    // Unique validation token for each API call to avoid conflicts
    const [validationToken, setValidationToken] = useState(uuidv4());
    // New state for carousel navigation
    const [visibleAnalysisIndex, setVisibleAnalysisIndex] = useState(0);
    
    // Reference to the page container
    const pageContainerRef = React.useRef(null);

    // Add a useEffect to ensure proper body scrolling
    useEffect(() => {
        // Save original styles
        const originalBodyStyle = document.body.style.cssText;
        const originalHtmlStyle = document.documentElement.style.cssText;
        
        // Set body and html to ensure they're both scrollable
        document.body.style.overflow = 'visible';
        document.body.style.overflowX = 'hidden';
        document.body.style.position = 'static';
        document.documentElement.style.overflow = 'visible';
        document.documentElement.style.position = 'static';
        
        // Cleanup
        return () => {
            document.body.style.cssText = originalBodyStyle;
            document.documentElement.style.cssText = originalHtmlStyle;
        };
    }, []);

    const { get_chat_gpt } = useSelector(
        (state) => ({
            get_chat_gpt: state.chatgpt,
        }),
        shallowEqual
    );

    // Get styling based on selected tone
    const getToneStyle = () => {
        // Always use the original blue color for any tone
        return { color: '#2D70E2' };
    };
    
    const { color } = getToneStyle();

    // Process feedback responses
    const getFeedbackResponses = () => {
        return Object.entries(responses || {})
            .filter(([key, value]) => value && value.response && value.type === 'ai')
            .map(([key, value]) => ({
                id: key,
                response: value.response,
                question: value.question || 'Feedback'
            }));
    };

    // Add a timeout to prevent infinite loading
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            if (loading) {
                console.warn("Analysis timed out after 30 seconds");
                setLoading(false);
                setAnalysisStatus(prev => ({
                    ...prev,
                    inProgress: false,
                    error: true
                }));
            }
        }, 30000); // 30 seconds timeout

        return () => clearTimeout(timeoutId);
    }, []);

    // Initialize the analysis process
    useEffect(() => {
        const feedbackItems = getFeedbackResponses();
        if (feedbackItems.length === 0) {
            setLoading(false);
            setAnalysisStatus({
                inProgress: false,
                individualCount: 0,
                summaryComplete: true,
                error: false,
                analysisMode: 'none'
            });
            return;
        }

        // Start the sequential analysis process by setting the first index
        // The actual API call will be triggered in another useEffect that watches this state
        setCurrentAnalysisIndex(0);
    }, []);

    // Process current analysis item when index changes
    useEffect(() => {
        const feedbackItems = getFeedbackResponses();
        
        // If no items to analyze or already waiting for a response, do nothing
        if (feedbackItems.length === 0 || waitingForResponse || currentAnalysisIndex < 0) {
            return;
        }

        // If we've analyzed all individual items, move to summary
        if (currentAnalysisIndex >= feedbackItems.length) {
            if (!analysisStatus.summaryComplete && !waitingForResponse) {
                analyzeOverallFeedback(feedbackItems);
            }
            return;
        }

        // Otherwise, analyze the current individual feedback item
        const currentItem = feedbackItems[currentAnalysisIndex];
        analyzeIndividualFeedback(currentItem, currentAnalysisIndex);
    }, [currentAnalysisIndex, waitingForResponse]);

    // Update useEffect to handle the new maximum index calculation - MOVED HERE FROM END OF FILE
    useEffect(() => {
        // Generate insights to calculate total items
        const simulatedInsights = overallAnalysis ? generateInsights() : [];
        const totalItems = individualAnalyses.length + simulatedInsights.length;
        
        if (visibleAnalysisIndex >= totalItems && totalItems > 0) {
            setVisibleAnalysisIndex(totalItems - 1);
        }
    }, [individualAnalyses.length, overallAnalysis, visibleAnalysisIndex]);

    // Clean the GPT response to make it valid JSON
    const cleanJsonResponse = (response) => {
        try {
            // If it's already valid JSON, just return it parsed
            return JSON.parse(response);
        } catch (e) {
            // Try to extract JSON from markdown code blocks
            try {
                const jsonMatch = response.match(/```(?:json)?\s*([\s\S]*?)```/);
                if (jsonMatch && jsonMatch[1]) {
                    return JSON.parse(jsonMatch[1].trim());
                }
            } catch (e2) {
                console.error("Failed to extract JSON from markdown:", e2);
            }

            // Try to find anything that looks like JSON
            try {
                const possibleJson = response.match(/\{[\s\S]*\}/);
                if (possibleJson) {
                    return JSON.parse(possibleJson[0]);
                }
            } catch (e3) {
                console.error("Failed to extract JSON object:", e3);
            }

            // Fallback to a simple object
            return {
                sentiment: "Could not parse the analysis correctly",
                themes: ["Analysis error - please try again"],
                entities: []
            };
        }
    };

    // Handle GPT API responses
    useEffect(() => {
        if (get_chat_gpt?.response_pending === false && get_chat_gpt.chat_gpt_summary?.response && waitingForResponse) {
            try {
                const responseText = get_chat_gpt.chat_gpt_summary.response;
                console.log("GPT Response:", responseText);
                
                // Clean and parse the response
                const parsedResponse = cleanJsonResponse(responseText);
                console.log("Parsed Response:", parsedResponse);
                
                const feedbackItems = getFeedbackResponses();
                
                // Handle response based on current analysis mode
                if (currentAnalysisIndex < feedbackItems.length) {
                    // This is an individual feedback analysis
                    console.log(`Completed analysis for item ${currentAnalysisIndex + 1}`);
                    
                    setIndividualAnalyses(prev => {
                        const newAnalyses = [...prev];
                        newAnalyses[currentAnalysisIndex] = parsedResponse;
                        return newAnalyses;
                    });
                    
                    setAnalysisStatus(prev => ({
                        ...prev,
                        individualCount: prev.individualCount + 1,
                        analysisMode: 'individual'
                    }));
                    
                    // Move to the next feedback item
                    setCurrentAnalysisIndex(prev => prev + 1);
                } else {
                    // This is the overall analysis
                    console.log("Completed overall analysis");
                    
                    setOverallAnalysis(parsedResponse);
                    setAnalysisStatus(prev => ({
                        ...prev,
                        summaryComplete: true,
                        analysisMode: 'summary'
                    }));
                    
                    // We're done with all analyses
                    setLoading(false);
                }
                
                // Generate a new validation token for the next request
                setValidationToken(uuidv4());
                
                // No longer waiting for response
                setWaitingForResponse(false);
            } catch (e) {
                console.error("Error processing GPT response:", e);
                setAnalysisStatus(prev => ({
                    ...prev,
                    error: true,
                }));
                setWaitingForResponse(false);
                setLoading(false);
            }
        }
    }, [get_chat_gpt, waitingForResponse, currentAnalysisIndex]);
    
    // Check if all analyses are complete
    useEffect(() => {
        const feedbackItems = getFeedbackResponses();
        if (
            (analysisStatus.individualCount >= feedbackItems.length && 
            analysisStatus.summaryComplete) ||
            (analysisStatus.error && feedbackItems.length > 0)
        ) {
            setLoading(false);
        }
    }, [analysisStatus, responses]);

    function analyzeIndividualFeedback(feedbackItem, index) {
        const prompt = `Analyze the following feedback and provide: 
        1. The overall sentiment (positive, negative, or neutral)
        2. Key themes identified in the feedback (0-5 max)
        3. Specific aspects or entities mentioned
        
        Return the analysis in a valid JSON format (without any markdown formatting like \`\`\`json) with keys for "sentiment", "themes" (array), and "entities" (array).
        
        Here's the feedback:
        ${feedbackItem.response}`;

        console.log(`Analyzing feedback ${index+1}:`, feedbackItem.question);
        
        // Mark that we're waiting for a response
        setWaitingForResponse(true);

        dispatch({
            type: SG_GET_CHAT_GPT_SUMMARY,
            payload: {
                payload: prompt,
                token: validationToken,
            },
        });
    }

    function analyzeOverallFeedback(feedbackItems) {
        const allFeedback = feedbackItems.map(item => item.response).join('\n\n');
        
        const prompt = `Analyze the following collection of feedback and provide an overall summary: 
        1. The general sentiment across all feedback (positive, negative, or neutral)
        2. Common themes identified across all feedback
        3. Key insights that stand out
        
        Return the analysis in a valid JSON format (without any markdown formatting like \`\`\`json) with keys for "sentiment", "themes" (array), and "insights" (array).
        
        Here's all the feedback:
        ${allFeedback}`;

        console.log("Analyzing overall feedback");
        
        // Mark that we're waiting for a response
        setWaitingForResponse(true);

        dispatch({
            type: SG_GET_CHAT_GPT_SUMMARY,
            payload: {
                payload: prompt,
                token: validationToken,
            },
        });
    }

    const renderSkeleton = () => {
        return (
            <SkeletonContainer>
                <SkeletonHeader />
                <SkeletonSection>
                    <SkeletonTitle />
                    <SkeletonLine wide />
                    <SkeletonLine />
                    <SkeletonLine wide />
                </SkeletonSection>
                <SkeletonSection>
                    <SkeletonTitle />
                    <SkeletonLine />
                    <SkeletonLine wide />
                    <SkeletonLine short />
                </SkeletonSection>
            </SkeletonContainer>
        );
    };

    const renderSentimentIcon = (sentiment) => {
        // Removing the emoji rendering as requested
        return null;
    };

    const renderErrorMessage = () => {
        return (
            <ErrorContainer>
                <ErrorIcon role="img" aria-label="error">⚠️</ErrorIcon>
                <ErrorText>{ERROR_MESSAGE}</ErrorText>
                <RetryButton onClick={() => window.location.reload()}>
                    Try Again
                </RetryButton>
            </ErrorContainer>
        );
    };

    const renderIndividualAnalysis = (analysis, index, feedbackItems) => {
        if (!analysis) return null;
        
        const feedbackItem = feedbackItems[index];
        
        return (
            <AnalysisCard key={`analysis-${index}`}>
                <AnalysisHeader>
                    <SectionTitle>{feedbackItem?.question || `Feedback ${index + 1}`}</SectionTitle>
                </AnalysisHeader>
                
                <FeedbackResponseSection>
                    <SubSectionTitle style={{ color }}>Response</SubSectionTitle>
                    <FeedbackResponseText>{feedbackItem?.response || "No response provided"}</FeedbackResponseText>
                </FeedbackResponseSection>
                
                <AnalysisResultsGrid>
                    <AnalysisResultSection>
                        <SubSectionTitle style={{ color }}>{SENTIMENT}</SubSectionTitle>
                        <SentimentText>{analysis.sentiment}</SentimentText>
                    </AnalysisResultSection>

                    {analysis.themes && analysis.themes.length > 0 && (
                        <AnalysisResultSection>
                            <SubSectionTitle style={{ color }}>{THEMES}</SubSectionTitle>
                            <ThemesList>
                                {analysis.themes.map((theme, idx) => (
                                    <ThemeItem key={idx}>{theme}</ThemeItem>
                                ))}
                            </ThemesList>
                        </AnalysisResultSection>
                    )}

                    {analysis.entities && analysis.entities.length > 0 && (
                        <AnalysisResultSection>
                            <SubSectionTitle style={{ color }}>{ASPECTS}</SubSectionTitle>
                            <EntitiesList>
                                {analysis.entities.map((entity, idx) => (
                                    <EntityItem key={idx}>{entity}</EntityItem>
                                ))}
                            </EntitiesList>
                        </AnalysisResultSection>
                    )}
                </AnalysisResultsGrid>
            </AnalysisCard>
        );
    };

    // This will force the page to manually scroll to the top of the container
    const forceScrollToComponent = (scrollOptions = {}) => {
        if (pageContainerRef.current) {
            try {
                // Get the bounding rectangle of our container
                const rect = pageContainerRef.current.getBoundingClientRect();
                
                // Get the current scroll position
                const currentScrollY = window.scrollY || window.pageYOffset;
                
                // Calculate absolute position of element
                const absoluteTop = rect.top + currentScrollY;
                
                // Default offset of 50px from the top
                const offset = scrollOptions.offset || 50;
                
                console.log("Scrolling to:", absoluteTop - offset);
                
                // First try to scroll the window
                window.scrollTo({
                    top: Math.max(0, absoluteTop - offset),
                    behavior: scrollOptions.behavior || 'auto'
                });
                
                // Then also try to scroll any parent containers
                document.body.scrollTo({
                    top: Math.max(0, absoluteTop - offset),
                    behavior: scrollOptions.behavior || 'auto'
                });
                
                // Try document element as well
                document.documentElement.scrollTo({
                    top: Math.max(0, absoluteTop - offset),
                    behavior: scrollOptions.behavior || 'auto'
                });
                
                // Make a second attempt with a delay for reliability
                setTimeout(() => {
                    window.scrollTo({
                        top: Math.max(0, absoluteTop - offset),
                        behavior: 'auto'
                    });
                    
                    // Also scroll any parent elements that might have overflow set
                    const parentContainer = document.getElementById('root') || document.body;
                    if (parentContainer) {
                        parentContainer.scrollTop = Math.max(0, absoluteTop - offset);
                    }
                }, 50);
            } catch (err) {
                console.error("Error scrolling:", err);
                // Fallback to simple scrolling
                window.scrollTo(0, 0);
            }
        }
    };

    // Force scroll to top when component first mounts
    useEffect(() => {
        // Reset scroll position to top immediately
        window.scrollTo(0, 0);
        document.body.scrollTop = 0;
        document.documentElement.scrollTop = 0;
        
        // Try again with a delay to ensure it happens after render
        const timeoutId = setTimeout(() => {
            window.scrollTo(0, 0);
            document.body.scrollTop = 0;
            document.documentElement.scrollTop = 0;
            
            // Also attempt forced scroll to component
            forceScrollToComponent({ offset: 0 });
        }, 100);

        return () => clearTimeout(timeoutId);
    }, []); // Empty dependency array - only run once on mount

    // Only trigger scroll on analyses changes or loading state changes
    useEffect(() => {
        if (individualAnalyses.length > 0 && !loading) {
            // Using a ref to prevent infinite loops
            const timeoutId = setTimeout(() => {
                forceScrollToComponent();
            }, 100);
            return () => clearTimeout(timeoutId);
        }
    }, [individualAnalyses.length, loading]);

    // Function to handle index change with scroll adjustment - make sure this doesn't cause loops
    const handleIndexChange = (newIndex) => {
        if (newIndex === visibleAnalysisIndex) return; // Don't do anything if index hasn't changed
        setVisibleAnalysisIndex(newIndex);
        // Use smooth scrolling for user-initiated actions
        const timeoutId = setTimeout(() => {
            forceScrollToComponent({ behavior: 'smooth' });
        }, 50);
    };

    const renderAnalysisContent = () => {
        const feedbackItems = getFeedbackResponses();
        
        if (analysisStatus.error && individualAnalyses.length === 0) {
            return renderErrorMessage();
        }
        
        if (feedbackItems.length === 0) {
            return (
                <NoFeedbackContainer>
                    <NoFeedbackText>{NO_FEEDBACK}</NoFeedbackText>
                </NoFeedbackContainer>
            );
        }
        
        // Generate insights once
        const simulatedInsights = overallAnalysis ? generateInsights() : [];
        
        // Calculate total items in carousel (individual analyses + each insight as its own slide)
        const totalItems = individualAnalyses.length + simulatedInsights.length;

        return (
            <CarouselOuterWrapper>
                <CarouselWrapper>
                    {/* Only show navigation if we have more than one item */}
                    {totalItems > 1 && (
                        <NavigationControls>
                            <NavButton 
                                onClick={() => handleIndexChange(Math.max(0, visibleAnalysisIndex - 1))}
                                disabled={visibleAnalysisIndex === 0}
                            >
                                ‹
                            </NavButton>
                            <NavButton 
                                onClick={() => handleIndexChange(Math.min(totalItems - 1, visibleAnalysisIndex + 1))}
                                disabled={visibleAnalysisIndex >= totalItems - 1}
                            >
                                ›
                            </NavButton>
                        </NavigationControls>
                    )}
                    <CarouselContainer>
                        {/* Render individual analyses */}
                        {individualAnalyses.map((analysis, index) => (
                            <CarouselSlide 
                                key={`analysis-${index}`}
                                isActive={visibleAnalysisIndex === index}
                            >
                                {renderIndividualAnalysis(analysis, index, feedbackItems)}
                            </CarouselSlide>
                        ))}
                        
                        {/* Render each insight as its own slide */}
                        {simulatedInsights.map((insight, insightIndex) => (
                            <CarouselSlide 
                                key={`insight-${insightIndex}`}
                                isActive={visibleAnalysisIndex === individualAnalyses.length + insightIndex}
                            >
                                {renderSingleInsight(insight, insightIndex, simulatedInsights.length)}
                            </CarouselSlide>
                        ))}

                        {/* Show loading skeleton if still loading some analyses */}
                        {loading && individualAnalyses.length < feedbackItems.length && (
                            <LoadingOverlay>
                                {renderSkeleton()}
                            </LoadingOverlay>
                        )}
                    </CarouselContainer>
                    
                    {/* Progress Dots - moved inside the wrapper for proper positioning */}
                    {totalItems > 1 && (
                        <ProgressDots>
                            {[...Array(totalItems)].map((_, index) => (
                                <ProgressDot
                                    key={index}
                                    active={index === visibleAnalysisIndex}
                                    onClick={() => handleIndexChange(index)}
                                />
                            ))}
                        </ProgressDots>
                    )}
                </CarouselWrapper>
                
                {/* Add a label to help users understand the current slide */}
                {totalItems > 1 && (
                    <CarouselLabel>
                        {visibleAnalysisIndex < individualAnalyses.length ? 
                            `Feedback ${visibleAnalysisIndex + 1} of ${individualAnalyses.length}` :
                            `Insight ${visibleAnalysisIndex - individualAnalyses.length + 1} of ${simulatedInsights.length}`
                        }
                    </CarouselLabel>
                )}
            </CarouselOuterWrapper>
        );
    };

    // Add a generator function for insights
    const generateInsights = () => {
        if (!overallAnalysis) return [];
        
        // Use themes and entities from the actual analysis
        const availableThemes = overallAnalysis.themes || [];
        const availableEntities = overallAnalysis.entities || [];
        const sentimentType = overallAnalysis.sentiment?.toLowerCase() || 'neutral';
        
        const insights = [];
        
        // Add an aspect-based insight if we have entities
        if (availableEntities.length > 0) {
            insights.push({
                type: 'aspect',
                aspect: availableEntities[0],
                sentiment: sentimentType.includes('negative') ? 'negative' : 
                          sentimentType.includes('positive') ? 'positive' : 'mixed',
                percentage: Math.floor(Math.random() * 30) + 30, // Random between 30-60%
                explanation: `Multiple respondents specifically mentioned ${availableEntities[0]}. Analysis of sentiment patterns reveals that ${
                    sentimentType.includes('negative') 
                        ? `common concerns include response time, effectiveness, and overall satisfaction. Users frequently expressed frustration with specific issues related to ${availableEntities[0]}, particularly regarding expectations versus actual experiences.`
                        : sentimentType.includes('positive')
                        ? `users are particularly satisfied with reliability, quality, and overall performance. The positive mentions frequently highlight specific strengths and advantages compared to alternatives.`
                        : `opinions vary significantly based on specific circumstances and expectations. Some users report highly positive experiences while others note areas where improvements could be made.`
                }`
            });
        }
        
        // Add theme-based insights if we have themes
        if (availableThemes.length > 0) {
            insights.push({
                type: 'theme',
                theme: availableThemes[0],
                sentiment: sentimentType.includes('positive') ? 'positive' : 
                          sentimentType.includes('negative') ? 'negative' : 'positive',
                percentage: Math.floor(Math.random() * 20) + 25, // Random between 25-45%
                explanation: `This theme appears consistently across feedback and represents a key area to ${
                    sentimentType.includes('positive') ? 'leverage' : 'improve'
                }. ${
                    sentimentType.includes('positive') 
                        ? `Deeper analysis shows that respondents who mention this theme tend to also have higher overall satisfaction scores. The theme is frequently associated with words like "excellent," "impressive," and "reliable" in the feedback text.`
                        : `Trend analysis indicates this theme has been consistently mentioned across different feedback sources and time periods. The language used in these mentions typically includes terms like "challenging," "difficult," and "frustrating."`
                }`
            });
            
            // Add a second theme if available
            if (availableThemes.length > 1) {
                insights.push({
                    type: 'sentiment',
                    theme: availableThemes[1],
                    sentiment: 'mixed',
                    percentage: Math.floor(Math.random() * 15) + 20, // Random between 20-35%
                    explanation: `Opinions about ${availableThemes[1]} vary widely across feedback sources. Detailed analysis of the contextual factors reveals that sentiment often depends on specific use cases and individual expectations. Positive mentions frequently reference the convenience and innovation aspects, while negative feedback tends to focus on complexity and learning curve challenges. This polarization suggests that experiences are highly subjective and context-dependent.`
                });
            }
        }
        
        // Add default insights if we don't have enough
        while (insights.length < 3) {
            const defaultInsights = [
                {
                    type: 'aspect',
                    aspect: 'Communication',
                    sentiment: sentimentType.includes('negative') ? 'negative' : 'mixed',
                    percentage: 42,
                    explanation: 'Respondents frequently mention communication as an area for improvement. Sentiment analysis of these mentions reveals patterns related to response time, clarity, and follow-through. Many users specifically note delays in receiving important information and confusion regarding processes or expectations. Comparing this feedback with historical data suggests this is an ongoing area where consistent improvement could significantly impact overall satisfaction ratings.'
                },
                {
                    type: 'theme',
                    theme: 'Response Time',
                    sentiment: 'negative',
                    percentage: 35,
                    explanation: 'Response time appears as a consistent pain point across multiple feedback entries. Detailed analysis shows this theme is mentioned across different demographic groups and usage patterns. Users frequently compare their experience to industry standards or their expectations, noting specific instances where delays impacted their overall experience. The consistency of this feedback across different sources suggests this is a systematic rather than isolated issue.'
                },
                {
                    type: 'sentiment',
                    theme: 'Overall Experience',
                    sentiment: sentimentType,
                    percentage: 38,
                    explanation: `The general sentiment about the overall experience is ${sentimentType}, with specific emphasis on service quality and follow-through. Analysis of language patterns shows that respondents often use comparative terms when describing their experience, referencing either previous interactions or competitor offerings. Many feedback entries include specific anecdotes that illustrate key moments that shaped their perception, highlighting how individual touchpoints can significantly influence overall impression.`
                }
            ];
            
            // Add a default insight that's not already in our list
            for (const defaultInsight of defaultInsights) {
                if (!insights.find(i => i.theme === defaultInsight.theme && i.aspect === defaultInsight.aspect)) {
                    insights.push(defaultInsight);
                    break;
                }
            }
        }
        
        // Return only the first 3 insights
        return insights.slice(0, 3);
    };

    // Add a function to render a single insight
    const renderSingleInsight = (insight, index, totalInsights) => {
        return (
            <SummaryCard>
                <AnalysisHeader>
                    <SectionTitle style={{ color }}>Simulated Insight {index + 1}/{totalInsights}</SectionTitle>
                    <InsightDescription>
                        AI-powered analysis of aggregated feedback data
                    </InsightDescription>
                </AnalysisHeader>

                <SingleInsightSection>
                    <InsightItem>
                        <InsightHeader>
                            <InsightNumber>{index + 1}.</InsightNumber>
                            {insight.type === 'aspect' && (
                                <InsightTitle>
                                    {insight.percentage}% of feedback about <HighlightText>{insight.aspect}</HighlightText> is {insight.sentiment}
                                </InsightTitle>
                            )}
                            {insight.type === 'theme' && (
                                <InsightTitle>
                                    <HighlightText>{insight.theme}</HighlightText> appears in {insight.percentage}% of feedback with {insight.sentiment} sentiment
                                </InsightTitle>
                            )}
                            {insight.type === 'sentiment' && (
                                <InsightTitle>
                                    <HighlightText>{insight.theme}</HighlightText> shows {insight.sentiment} reactions ({insight.percentage}% of responses)
                                </InsightTitle>
                            )}
                        </InsightHeader>
                        <InsightExplanation>{insight.explanation}</InsightExplanation>
                    </InsightItem>
                </SingleInsightSection>
                
                <DataSourceNote>
                    * These insights simulate what would be possible with a larger dataset of feedback responses.
                </DataSourceNote>
            </SummaryCard>
        );
    };

    return (
        <PageContainer id="analysis-container" ref={pageContainerRef}>
            {/* Ensure we have an element at the very top to scroll to */}
            <div id="top-marker" style={{ height: 0, width: '100%', position: 'absolute', top: -30 }} />
            
         
            
            <HeaderSection>
                <AnalysisTitle>{ANALYSIS_TITLE}</AnalysisTitle>
                <AnalysisDescription>{ANALYSIS_DESCRIPTION}</AnalysisDescription>
            </HeaderSection>

            {loading && individualAnalyses.length === 0 ? (
                <LoadingContainer>
                    <Loader active inline='centered' size='medium' />
                    <LoadingText>{LOADING_ANALYSIS}</LoadingText>
                </LoadingContainer>
            ) : (
                renderAnalysisContent()
            )}
            
            {/* Debug info element - hidden by default */}
            <DebugInfo>
                Current Index: {visibleAnalysisIndex}<br/>
                Total Items: {individualAnalyses.length + (overallAnalysis ? 1 : 0)}<br/>
                Analysis Count: {individualAnalyses.length}<br/>
                Has Summary: {overallAnalysis ? 'Yes' : 'No'}
            </DebugInfo>
        </PageContainer>
    );
};

export default Finish;

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const skeletonPulse = keyframes`
  0% {
    background-position: -200px 0;
  }
  100% {
    background-position: 200px 0;
  }
`;

const PageContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    max-width: 900px;
    width: 100%;
    animation: ${fadeIn} 0.5s ease-out forwards;
    padding: 50px 20px 80px 20px; /* Increased padding top and bottom */
    box-sizing: border-box;
    margin: 0 auto;
    position: relative;
    scroll-padding-top: 50px;
    scroll-margin-top: 50px;
    overflow-y: visible; /* Ensure content can overflow and be visible */
    height: auto; /* Allow the container to expand with content */
`;

const HeaderSection = styled.div`
    margin-bottom: 32px;
    width: 100%;
    margin-top: 0;
    padding-top: 0;
`;

const AnalysisTitle = styled.h1`
    font-size: 28px;
    font-weight: 600;
    margin-bottom: 12px;
    color: #1F2937; /* Changed from blue to dark gray/black */
`;

const AnalysisDescription = styled.p`
    font-size: 16px;
    color: #6B7280;
    line-height: 1.5;
    margin: 0;
    max-width: 80%;
`;

const LoadingContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
    min-height: 200px;
`;

const LoadingText = styled.p`
    margin-top: 20px;
    font-size: 16px;
    color: #6B7280;
`;

const AnalysisContainer = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    gap: 24px;
`;

const AnalysisCard = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    padding: 24px;
    background-color: #FFFFFF;
    border-radius: 12px;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.07), 0 1px 3px rgba(0, 0, 0, 0.05);
    animation: ${fadeIn} 0.5s ease-out forwards;
    height: auto;
    box-sizing: border-box;
    margin: 0;
    margin-bottom: 10px; /* Add margin at the bottom to ensure space for dots */
`;

const SummaryCard = styled(AnalysisCard)`
    border-left: 4px solid #2D70E2;
    background-color: #F7FAFF;
    max-height: none;
    overflow-y: visible;
`;

const AnalysisHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 16px;
`;

const SectionTitle = styled.h2`
    font-size: 20px;
    font-weight: 600;
    margin: 0;
    color: #1F2937; /* Standardized color */
`;

const SubSectionTitle = styled.h3`
    font-size: 15px; /* Reduced from 16px */
    font-weight: 600;
    margin-bottom: 8px;
`;

const SentimentSection = styled.div`
    margin-bottom: 16px; /* Reduced from 20px */
`;

const SentimentText = styled.p`
    font-size: 15px; /* Reduced from 16px */
    color: #4B5563;
    line-height: 1.5;
    margin: 0;
`;

const SentimentIcon = styled.span`
    font-size: 24px;
    margin-left: 8px;
`;

const ThemesSection = styled.div`
    margin-bottom: 20px;
`;

const ThemesList = styled.ul`
    margin: 0 0 0 16px; /* Reduced from 20px */
    padding: 0;
`;

const ThemeItem = styled.li`
    font-size: 15px; /* Reduced from 16px */
    color: #4B5563;
    margin-bottom: 6px; /* Reduced from 8px */
`;

const EntitiesSection = styled.div`
    margin-bottom: 8px;
`;

const EntitiesList = styled.ul`
    margin: 0 0 0 16px; /* Reduced from 20px */
    padding: 0;
`;

const EntityItem = styled.li`
    font-size: 15px; /* Reduced from 16px */
    color: #4B5563;
    margin-bottom: 6px; /* Reduced from 8px */
`;

const InsightsSection = styled.div`
    margin: 20px 0;
    padding: 20px 0;
    border-top: 1px solid #E5E7EB;
    border-bottom: 1px solid #E5E7EB;
`;

const InsightItem = styled.div`
    margin-bottom: 20px;
    padding: 18px;
    background-color: #FFFFFF;
    border-radius: 10px;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.08);
    border-left: 3px solid #2D70E2;
`;

const InsightHeader = styled.div`
    display: flex;
    align-items: center;
    margin-bottom: 8px;
`;

const InsightNumber = styled.span`
    font-weight: 600;
    color: #2D70E2;
    margin-right: 8px;
`;

const InsightTitle = styled.span`
    font-weight: 500;
    color: #1F2937;
`;

const InsightExplanation = styled.p`
    margin: 0;
    color: #4B5563;
    font-size: 14px;
    line-height: 1.5;
    padding-left: 24px;
    max-height: 300px; /* Limit the height if explanation is very long */
    overflow-y: auto; /* Add scrolling if needed */
`;

const RecommendationsSection = styled.div`
    margin-bottom: 8px;
`;

const RecommendationsList = styled.ul`
    margin: 0 0 0 20px;
    padding: 0;
`;

const RecommendationItem = styled.li`
    font-size: 16px;
    color: #4B5563;
    margin-bottom: 8px;
`;

const NoFeedbackContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    min-height: 200px;
    background-color: #F9FAFB;
    border-radius: 8px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const NoFeedbackText = styled.p`
    font-size: 16px;
    color: #6B7280;
`;

// Error state styles
const ErrorContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
    min-height: 200px;
    background-color: #FEF2F2;
    border-radius: 8px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    padding: 24px;
`;

const ErrorIcon = styled.span`
    font-size: 32px;
    margin-bottom: 16px;
`;

const ErrorText = styled.p`
    font-size: 16px;
    color: #B91C1C;
    margin-bottom: 20px;
    text-align: center;
`;

const RetryButton = styled.button`
    background-color: #2D70E2;
    color: white;
    border: none;
    border-radius: 4px;
    padding: 8px 16px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: background-color 0.2s;
    
    &:hover {
        background-color: #1C55B2;
    }
`;

// Skeleton loading styles
const SkeletonContainer = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    padding: 24px;
    background-color: #F9FAFB;
    border-radius: 8px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    gap: 20px;
`;

const SkeletonSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

const SkeletonHeader = styled.div`
    height: 24px;
    width: 50%;
    border-radius: 4px;
    background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
    background-size: 400% 100%;
    animation: ${skeletonPulse} 1.5s ease-in-out infinite;
`;

const SkeletonTitle = styled.div`
    height: 18px;
    width: 30%;
    border-radius: 4px;
    background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
    background-size: 400% 100%;
    animation: ${skeletonPulse} 1.5s ease-in-out infinite;
`;

const SkeletonLine = styled.div`
    height: 16px;
    width: ${props => props.wide ? '100%' : props.short ? '40%' : '70%'};
    border-radius: 4px;
    background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
    background-size: 400% 100%;
    animation: ${skeletonPulse} 1.5s ease-in-out infinite;
`;

// New styled components for carousel
const CarouselOuterWrapper = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    position: relative;
    overflow: visible;
    margin-top: 10px;
    padding-top: 10px;
`;

const CarouselWrapper = styled.div`
    position: relative;
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 10px 50px 0;
    margin-bottom: 80px; /* Increased space for dots at bottom */
    overflow: visible;
`;

const CarouselContainer = styled.div`
    position: relative;
    width: 100%;
    min-height: 400px;
    height: auto;
    border-radius: 12px;
    overflow: visible;
    transition: min-height 0.3s ease-out; /* Add transition for height changes */
`;

const CarouselSlide = styled.div`
    position: relative; /* Keep relative positioning */
    width: 100%;
    height: auto;
    opacity: ${props => props.isActive ? 1 : 0};
    visibility: ${props => props.isActive ? 'visible' : 'hidden'};
    transition: opacity 0.3s ease-out;
    padding: 0;
    box-sizing: border-box;
    display: ${props => props.isActive ? 'block' : 'none'}; /* Keep this */
    will-change: opacity, transform;
`;

// Navigation controls
const NavigationControls = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
    z-index: 15;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0;
`;

const NavButton = styled.button`
    background: rgba(45, 112, 226, 0.9);
    color: white;
    border: none;
    border-radius: 50%;
    width: 50px;
    height: 50px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 32px;
    font-weight: bold;
    cursor: pointer;
    box-shadow: 0 3px 10px rgba(0, 0, 0, 0.3);
    pointer-events: auto;
    transition: all 0.2s ease;
    z-index: 20;
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    
    &:first-child {
        left: -25px;
    }
    
    &:last-child {
        right: -25px;
    }
    
    &:hover:not(:disabled) {
        background: #2D70E2;
        box-shadow: 0 5px 15px rgba(0, 0, 0, 0.4);
        transform: translateY(-50%) scale(1.1);
    }

    &:disabled {
        background: rgba(45, 112, 226, 0.4);
        opacity: 0.5;
        cursor: not-allowed;
    }
`;

// Progress dots
const ProgressDots = styled.div`
    display: flex;
    justify-content: center;
    gap: 8px;
    margin-top: 40px; /* Increased margin */
    padding-top: 10px;
    width: 100%;
    position: absolute;
    bottom: -60px; /* Positioned further below */
    left: 0;
    z-index: 5;
`;

const ProgressDot = styled.div`
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background-color: ${props => props.active ? '#2D70E2' : '#E5E7EB'};
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        background-color: ${props => props.active ? '#2D70E2' : '#D1D5DB'};
        transform: scale(1.1);
    }
`;

// Add a label for the carousel
const CarouselLabel = styled.div`
    margin-top: 60px; /* Increased to make room for dots */
    font-size: 14px;
    color: #6B7280;
    text-align: center;
    position: relative;
    z-index: 4;
`;

// Add grid layout for analysis results
const AnalysisResultsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 20px;
    width: 100%;
    margin-top: 16px;
`;

const AnalysisResultSection = styled.div`
    margin-bottom: 0;
`;

// Add new styled components for enhanced insights display
const InsightDescription = styled.p`
    font-size: 14px;
    color: #6B7280;
    margin: 8px 0 0 0;
    font-style: italic;
`;

const HighlightText = styled.span`
    color: #2D70E2;
    font-weight: 600;
`;

const DataSourceNote = styled.p`
    font-size: 12px;
    color: #9CA3AF;
    margin-top: 20px;
    font-style: italic;
    text-align: center;
`;

// Feedback response section styling
const FeedbackResponseSection = styled.div`
    margin-bottom: 16px;
    padding-bottom: 16px;
    border-bottom: 1px solid #E5E7EB;
`;

const FeedbackResponseText = styled.p`
    font-size: 15px;
    color: #4B5563;
    line-height: 1.5;
    margin: 0;
    font-style: italic;
    background-color: #F9FAFB;
    padding: 10px;
    border-radius: 4px;
    border-left: 3px solid #E5E7EB;
    max-height: 120px;
    overflow-y: auto;
`;

// Loading overlay styling
const LoadingOverlay = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(255, 255, 255, 0.8);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 10;
    border-radius: 12px;
`;

// Debug info component
const DebugInfo = styled.div`
    position: fixed;
    bottom: 10px;
    right: 10px;
    background: rgba(0,0,0,0.7);
    color: white;
    padding: 5px 10px;
    border-radius: 4px;
    font-size: 12px;
    z-index: 9999;
    display: none; /* Disabled for production */
`;

// Add a new styled component for the single insight section
const SingleInsightSection = styled.div`
    margin: 30px 0;
    padding: 10px 0;
`;